import React, { useEffect, useMemo, useState } from 'react';
import { Select, Button, Loader } from '@mantine/core';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';

import { GET_DROPDOWN_USERS, GET_USERS } from '../../apollo/queries/user';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import { User, OpsStore, HierarchyUser } from '../../codegen/graphql';
import { UPDATE_USER } from '../../apollo/mutations/user';
import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { GET_STORES } from '../../apollo/queries/store';
import { UserRole } from '../../types/users';

type UserSelectItem = {
    value: string;
    disabled: boolean;
    label: string;
};

type Props = {
    readonly storeManager: HierarchyUser | null;
    readonly store: OpsStore;
    readonly close: () => void;
};

const STORE_MANAGER_DROPDOWN_LIMIT = 20;

function AddStoreManagerForm({ storeManager, store, close }: Props) {
    const [searchValue, setSearchValue] = useState(
        storeManager
            ? `${storeManager.firstName} ${storeManager.lastName}`
            : '',
    );
    const [debouncedSearch] = useDebouncedValue(searchValue, 300);

    const {
        data: usersData,
        loading: loadingUsers,
        error: usersError,
    } = useQuery(GET_DROPDOWN_USERS, {
        variables: {
            roleFilter: UserRole.STORE_MANAGER,
            nameSearch: debouncedSearch.trim() || undefined,
            limit: STORE_MANAGER_DROPDOWN_LIMIT,
            includeInactive: true,
        },
        fetchPolicy: 'cache-and-network',
    });

    const [updateUser, { loading: updatingUser, error: updateUserError }] =
        useMutation(UPDATE_USER, {
            refetchQueries: [
                GET_DROPDOWN_USERS,
                GET_USERS,
                GET_OPS_HIERARCHY,
                GET_STORES,
            ],
            awaitRefetchQueries: true,
        });

    const users = useMemo<UserSelectItem[]>(() => {
        if (!usersData?.getUsers) {
            return [];
        }
        return usersData.getUsers
            .map(
                ({
                    id,
                    firstName,
                    lastName,
                    managerStore,
                    designation,
                }: User) => {
                    return {
                        value: id,
                        disabled: !!managerStore,
                        label: `${firstName} ${lastName}${
                            managerStore
                                ? ` (${designation} at ${managerStore.name})`
                                : ''
                        }`,
                    };
                },
            )
            .sort((a: UserSelectItem, b: UserSelectItem) => {
                if (a.disabled !== b.disabled) {
                    return a.disabled ? 1 : -1;
                }
                return a.label.localeCompare(b.label);
            });
    }, [usersData]);

    const { onSubmit, reset, ...form } = useForm({
        initialValues: {
            storeManagerId: storeManager?.id ?? '',
            storeId: store?.id ?? '',
        },
        validate: {
            storeManagerId: value =>
                !value ? 'Store Manager is required' : null,
            storeId: value => (!value ? 'Store is required' : null),
        },
    });

    const handleSubmit = onSubmit(async values => {
        try {
            await updateUser({
                variables: {
                    input: {
                        id: values.storeManagerId,
                        managerStoreId: store.id,
                    },
                },
            });
            showSuccessNotification('Store manager added successfully');
            close();
            reset();
        } catch (error) {
            form.setFieldError('storeId', 'Error adding store manager');
        }
    });

    const selectedManager = useMemo(() => {
        if (!form.values.storeManagerId || !usersData?.getUsers) return null;
        return usersData.getUsers.find(
            (user: User) => user.id === form.values.storeManagerId,
        );
    }, [form.values.storeManagerId, usersData]);

    const confirmDisabled = useMemo(() => {
        return (
            !form.values.storeManagerId ||
            !form.values.storeId ||
            !!selectedManager?.managerStore
        );
    }, [
        form.values.storeManagerId,
        form.values.storeId,
        selectedManager?.managerStore,
    ]);

    useEffect(() => {
        if (usersError) {
            showErrorNotification('Error fetching users');
        }
        if (updateUserError) {
            form.setFieldError('storeId', 'Error adding store manager');
        }
    }, [usersError, updateUserError]);

    useEffect(() => {
        if (storeManager) {
            form.setFieldValue('storeManagerId', storeManager?.id ?? '');
            setSearchValue(
                `${storeManager.firstName} ${storeManager.lastName}`,
            );
        }
    }, [storeManager]);

    return (
        <form onSubmit={handleSubmit} className="p-4">
            <div className="flex flex-col gap-4">
                <Select
                    name="storeManagerId"
                    searchable
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    data={users}
                    label="Select store manager"
                    placeholder="Search by name..."
                    description="Managers already assigned to a store are shown but can't be selected. To add one, ask their current DM to remove them from their store first."
                    inputWrapperOrder={[
                        'label',
                        'input',
                        'description',
                        'error',
                    ]}
                    nothingFoundMessage={
                        loadingUsers ? 'Searching...' : 'No users found'
                    }
                    rightSection={loadingUsers ? <Loader size="xs" /> : null}
                    comboboxProps={{ withinPortal: true, zIndex: 1000 }}
                    {...form.getInputProps('storeManagerId')}
                />
            </div>

            <Button
                type="submit"
                size="md"
                className="mt-4"
                disabled={confirmDisabled}
                loading={updatingUser}
            >
                Confirm
            </Button>
        </form>
    );
}

export default AddStoreManagerForm;
